/**
 * Which fields of a keybook key are SECRET — the one rule for the detail panel's mask (`ui/credential-type-ui.ts`) and
 * for what a token's file read is masked with and its file write refused for (#1297, `tenantBoundSecretValues`): a
 * username, a host or a region is not a secret, and refusing every file that says "postgres" would block ordinary work.
 *
 * Fixed-shape types: their stored fields from THE one type list (`field-match.ts`, #1324). `github`: nothing the panel
 * shows (its token is server-managed). `custom` / `oauth` / legacy / unknown: the field's own `fieldsMeta.sensitive`
 * when the person set it, else the field-name rule below.
 */
import { classifyFieldName, fixedCredentialFields } from './field-match.js'
import type { KeybookEntryData } from './types.js'

/** The field-name rule for a field nobody marked: the shared rule's secrets (`pwd`, `pin`, `passphrase`,
 *  `credential`…), plus the substrings that always masked, plus the spellings of a DSN, a webhook, an auth header and
 *  a signed URL — masking more is the safe direction for both readers. */
export function isFieldNameSensitive(name: string): boolean {
  const lower = name.toLowerCase()
  return classifyFieldName(name).kind === 'secret'
    || ['pass', 'pwd', 'key', 'token', 'secret', 'pat', 'private_key', 'json', 'certificate', 'connection_string',
      'access_token', 'refresh_token', 'app_password', 'credential', 'dsn', 'webhook', 'auth', 'signature']
      .some(s => lower.includes(s))
}

/** The fields of a fixed-shape type, and whether each is secret; `[]` for github; null for anything else. */
export function staticFieldSensitivity(credentialType: string): { name: string; sensitive: boolean }[] | null {
  if (credentialType === 'github') return []
  const fixed = fixedCredentialFields(credentialType)
  return fixed ? fixed.map(f => ({ name: f.name, sensitive: f.kind === 'secret' })) : null
}

/** Is `name` a secret field of a key of this type? (A fixed type's field outside its schema — e.g. github's stored
 *  token — goes by the name rule.) */
export function isSecretKeyField(credentialType: string, name: string, data?: Pick<KeybookEntryData, 'fieldsMeta'> | null): boolean {
  const known = staticFieldSensitivity(credentialType)?.find(f => f.name === name)
  if (known) return known.sensitive
  return data?.fieldsMeta?.[name]?.sensitive ?? isFieldNameSensitive(name)
}
